import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/projects/Civi_Circle_Admin/components/ui/card";
import { Button } from "@/projects/Civi_Circle_Admin/components/ui/button";
import { Badge } from "@/projects/Civi_Circle_Admin/components/ui/badge";
import { Input } from "@/projects/Civi_Circle_Admin/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/projects/Civi_Circle_Admin/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/projects/Civi_Circle_Admin/components/ui/dialog";
import { Textarea } from "@/projects/Civi_Circle_Admin/components/ui/textarea";
import {
  Search,
  CheckCircle,
  XCircle,
  Eye,
  Clock,
  User,
  Mail,
  Building,
  Calendar,
  MessageSquare,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import MemberService from "@/services/APIs/MemberService";
import RosterService from "@/services/APIs/RosterService";
import { Member } from "@/utils/types/member";

export function MemberInquiriesTab() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInquiry, setSelectedInquiry] = useState<Member | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  /** After update-member-status succeeds, show API `data.verification` in Actions (and Verification) until refetch. */
  const [verificationFromStatusUpdate, setVerificationFromStatusUpdate] =
    useState<Record<string, string>>({});

  // Fetch members data from API with member_status=false
  const { data, isLoading } = useQuery({
    queryKey: ["members-inquiries"],
    queryFn: () =>
      MemberService.getAllMembers({
        page: 1,
        pageSize: 1000,
        member_status: false,
      }),
  });

  // Fetch member status counts from API
  const { data: statusCounts } = useQuery({
    queryKey: ["member-status-counts"],
    queryFn: () => RosterService.getMemberStatusCounts(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Mutation for updating member status (approve/reject)
  const updateMemberStatusMutation = useMutation({
    mutationFn: ({
      memberId,
      status,
      reason,
    }: {
      memberId: string;
      status?: string;
      reason?: string;
    }) => RosterService.updateMemberStatus(memberId, status, reason),
    onSuccess: (response, variables) => {
      const payload = response?.data;
      const memberId = payload?.member_id ?? variables.memberId;
      const verification = payload?.verification;
      if (
        response?.success &&
        memberId &&
        verification !== undefined &&
        verification !== null &&
        String(verification).trim() !== ""
      ) {
        setVerificationFromStatusUpdate(prev => ({
          ...prev,
          [memberId]: String(verification),
        }));
      }

      // Invalidate and refetch queries to update the UI
      queryClient.invalidateQueries({ queryKey: ["members-inquiries"] });
      queryClient.invalidateQueries({ queryKey: ["member-status-counts"] });

      if (variables.status === "Rejected") {
        toast.success("Member inquiry rejected successfully");
        setIsRejectDialogOpen(false);
        setRejectionReason("");
      } else {
        toast.success("Member inquiry approved successfully");
      }
      setIsDetailOpen(false);
    },
    onError: (error: any, variables) => {
      console.error("Error updating member status:", error);
      const action = variables.status === "Rejected" ? "reject" : "approve";
      toast.error(
        error?.message ??
          `Failed to ${action} member inquiry. Please try again.`
      );
    },
  });

  // Map API data to inquiry format for display
  const inquiries = useMemo(() => {
    const members = data?.results ?? [];
    return members.map((member: Member) => {
      // Map member status to inquiry status (directory list: member_status may be "Rejected", "Active", etc.)
      let status: "pending" | "approved" | "rejected" = "pending";
      const memberStatus = String(member.member_status ?? "").toLowerCase();
      if (memberStatus === "active") {
        status = "approved";
      } else if (
        memberStatus === "rejected" ||
        memberStatus === "inactive" ||
        memberStatus === "suspended"
      ) {
        status = "rejected";
      }

      // Check verification field (from API response reference)
      const verificationVal = member.verification ?? member.member_verified;
      const verificationStr = String(verificationVal ?? "").toLowerCase();
      if (verificationStr === "rejected") {
        status = "rejected";
      } else if (
        verificationStr === "approved" ||
        verificationStr === "verified"
      ) {
        status = "approved";
      }

      let verificationText = "Not Verified";
      if (status === "rejected" || verificationStr === "rejected") {
        verificationText = "Rejected";
      } else if (
        status === "approved" ||
        verificationStr === "verified" ||
        verificationStr === "approved" ||
        verificationVal === true ||
        verificationVal === 1
      ) {
        verificationText = "Verified";
      } else if (
        verificationVal &&
        typeof verificationVal === "string" &&
        verificationVal.toLowerCase() !== "yes" &&
        verificationVal.toLowerCase() !== "true" &&
        verificationVal.toLowerCase() !== "false"
      ) {
        verificationText = verificationVal;
      }

      return {
        id: member.member_id ?? "",
        fullName:
          `${member.member_first_name ?? ""} ${member.member_last_name ?? ""}`.trim(),
        email: member.member_email ?? "",
        phone: member.member_phone_number ?? member.member_phone ?? "",
        organization: member.organization_name ?? "Unknown",
        department: member.department ?? "N/A",
        position: member.position ?? "",
        reason: "Member inquiry from directory",
        submittedAt: member.created_on ?? new Date().toISOString(),
        status,
        verificationText,
        memberData: member,
      };
    });
  }, [data?.results]);

  // Use API counts if available, otherwise fallback to filtered inquiries
  const pendingCount =
    statusCounts?.pending ??
    inquiries.filter(i => i.status === "pending").length;

  const rejectedCount =
    statusCounts?.rejected ??
    inquiries.filter(i => i.status === "rejected").length;

  const filteredInquiries = inquiries.filter(inquiry => {
    const matchesSearch =
      inquiry.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inquiry.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inquiry.organization.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleApprove = (memberId: string) => {
    if (!memberId) {
      toast.error("Member ID is required");
      return;
    }
    updateMemberStatusMutation.mutate({ memberId, status: "Approved" });
  };

  const handleReject = () => {
    if (selectedInquiry) {
      const memberId = selectedInquiry.member_id;
      if (!memberId) {
        toast.error("Member ID is required");
        return;
      }

      if (!rejectionReason.trim()) {
        toast.error("Please provide a reason for rejection");
        return;
      }

      updateMemberStatusMutation.mutate({
        memberId,
        status: "Rejected",
        reason: rejectionReason,
      });
    }
  };

  const openRejectDialog = (inquiry: Member) => {
    setSelectedInquiry(inquiry);
    setIsRejectDialogOpen(true);
  };

  const statusConfig = {
    pending: {
      label: "Pending",
      variant: "outline" as const,
      color: "text-warning",
    },
    approved: {
      label: "Approved",
      variant: "default" as const,
      color: "text-success",
    },
    rejected: {
      label: "Rejected",
      variant: "destructive" as const,
      // Must stay foreground on destructive bg; text-destructive = red on red (invisible)
      color: "text-destructive-foreground",
    },
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Clock className="text-warning h-4 w-4" />
              <span className="text-sm text-muted-foreground">Pending</span>
            </div>
            <div className="text-warning mt-1 text-2xl font-bold">
              {pendingCount}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-destructive" />
              <span className="text-sm text-muted-foreground">Rejected</span>
            </div>
            <div className="mt-1 text-2xl font-bold text-destructive">
              {rejectedCount}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inquiries Table */}
      <Card>
        <CardHeader>
          <CardTitle>Member Inquiries</CardTitle>
          <CardDescription>
            Review and approve incoming membership requests
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or organization..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">Loading...</div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Applicant</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Position</TableHead>
                  <TableHead>Skills</TableHead>
                  <TableHead>Verification</TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInquiries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center">
                      <div className="text-muted-foreground">
                        No inquiries found
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInquiries.map(inquiry => {
                    const member = inquiry.memberData;
                    const skills = member.skillinterests;
                    const apiVerification =
                      verificationFromStatusUpdate[inquiry.id];
                    const verificationDisplay =
                      apiVerification ?? inquiry.verificationText;

                    return (
                      <TableRow key={inquiry.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{inquiry.fullName}</p>
                            <p className="text-sm text-muted-foreground">
                              {inquiry.email}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>{inquiry.organization}</TableCell>
                        <TableCell>{inquiry.department}</TableCell>
                        <TableCell>{inquiry.position}</TableCell>
                        <TableCell>
                          {!skills ||
                          (Array.isArray(skills) && skills.length === 0) ? (
                            <span className="text-muted-foreground">-</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(Array.isArray(skills) ? skills : [skills]).map(
                                (skill: string) => (
                                  <Badge
                                    key={`${inquiry.id}-${skill}`}
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    {skill}
                                  </Badge>
                                )
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {verificationDisplay === "Verified" ||
                            verificationDisplay === "Approved" ? (
                              <>
                                <CheckCircle className="h-4 w-4 text-green-600" />
                                <span className="text-sm text-foreground">
                                  {verificationDisplay}
                                </span>
                              </>
                            ) : verificationDisplay === "Rejected" ? (
                              <>
                                <XCircle className="h-4 w-4 text-red-600" />
                                <span className="text-sm text-red-600">
                                  {verificationDisplay}
                                </span>
                              </>
                            ) : (
                              <>
                                <XCircle className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-muted-foreground">
                                  {verificationDisplay}
                                </span>
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {inquiry.phone ??
                            member.member_phone_number ??
                            member.member_phone ??
                            "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2 text-right">
                            {apiVerification ? (
                              <span
                                className={
                                  apiVerification === "Rejected"
                                    ? "text-sm font-medium text-destructive"
                                    : apiVerification === "Approved"
                                      ? "text-success text-sm font-medium"
                                      : "text-sm font-medium text-foreground"
                                }
                              >
                                {apiVerification}
                              </span>
                            ) : inquiry.status !== "pending" ? (
                              <Badge
                                variant={statusConfig[inquiry.status].variant}
                                className={statusConfig[inquiry.status].color}
                              >
                                {statusConfig[inquiry.status].label}
                              </Badge>
                            ) : (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedInquiry(inquiry.memberData);
                                    setIsDetailOpen(true);
                                  }}
                                  title="View Details"
                                  className="h-8 w-8 p-0 hover:bg-gray-100"
                                >
                                  <Eye className="h-4 w-4 text-gray-700" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-green-600 hover:bg-green-50 hover:text-green-700"
                                  onClick={() =>
                                    handleApprove(
                                      inquiry.memberData.member_id ?? inquiry.id
                                    )
                                  }
                                  title="Accept"
                                  disabled={
                                    updateMemberStatusMutation.isPending
                                  }
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
                                  onClick={() =>
                                    openRejectDialog(inquiry.memberData)
                                  }
                                  title="Reject"
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Member Inquiry Details</DialogTitle>
            <DialogDescription>
              Review the membership request details
            </DialogDescription>
          </DialogHeader>
          {selectedInquiry && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 rounded-lg bg-muted/50 p-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">
                    {`${selectedInquiry.member_first_name ?? ""} ${selectedInquiry.member_last_name ?? ""}`.trim()}
                  </p>
                  <Badge
                    variant={
                      statusConfig[
                        selectedInquiry.member_status === "active"
                          ? "approved"
                          : "pending"
                      ].variant
                    }
                  >
                    {selectedInquiry.member_status === "active"
                      ? "Approved"
                      : "Pending"}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    Email
                  </div>
                  <p className="text-sm font-medium">
                    {selectedInquiry.member_email ?? "-"}
                  </p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Building className="h-4 w-4" />
                    Organization
                  </div>
                  <p className="text-sm font-medium">
                    {selectedInquiry.organization_name ?? "-"}
                  </p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="h-4 w-4" />
                    Position
                  </div>
                  <p className="text-sm font-medium">
                    {selectedInquiry.position ?? "-"}
                  </p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    Submitted
                  </div>
                  <p className="text-sm font-medium">
                    {selectedInquiry.created_on
                      ? format(
                          new Date(selectedInquiry.created_on),
                          "MMM d, yyyy 'at' HH:mm"
                        )
                      : "-"}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MessageSquare className="h-4 w-4" />
                  Reason for Joining
                </div>
                <p className="rounded-lg bg-muted/50 p-3 text-sm">
                  Member inquiry from directory
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            {selectedInquiry && selectedInquiry.member_status !== "active" && (
              <>
                <Button
                  variant="outline"
                  onClick={() => openRejectDialog(selectedInquiry)}
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Reject
                </Button>
                <Button
                  onClick={() => handleApprove(selectedInquiry.member_id ?? "")}
                  disabled={updateMemberStatusMutation.isPending}
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {updateMemberStatusMutation.isPending
                    ? "Approving..."
                    : "Approve"}
                </Button>
              </>
            )}
            {selectedInquiry && selectedInquiry.member_status === "active" && (
              <Button variant="outline" onClick={() => setIsDetailOpen(false)}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Inquiry</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this membership request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsRejectDialogOpen(false)}
              disabled={updateMemberStatusMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={updateMemberStatusMutation.isPending}
            >
              {updateMemberStatusMutation.isPending
                ? "Rejecting..."
                : "Reject Inquiry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
